import { useState, useEffect } from "react";

import { Shield, Sparkles, X } from "lucide-react";

import { CommunicationCodeSelector } from "@/components/auth/CommunicationCodeSelector";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

export const CommunicationCodeOnboarding = () => {
  const { user } = useAuth();
  const { execute: invoke, isLoading } = useEdgeFunction({ auth: true });
  const [showDialog, setShowDialog] = useState(false);
  const [communicationCode, setCommunicationCode] = useState<string>("");

  useEffect(() => {
    const checkCommunicationCode = async () => {
      if (!user?.id) return;

      // Check if user has a communication code and if they've seen this onboarding
      const { data: profile } = await supabase
        .from("profiles")
        .select("communication_code")
        .eq("id", user.id)
        .single();

      // Only show if they don't have a code
      if (!profile?.communication_code) {
        // Check if they've dismissed this before (using localStorage for now)
        const dismissed = localStorage.getItem(`comm-code-onboarding-${user.id}`);
        if (!dismissed) {
          setShowDialog(true);
        }
      }
    };

    checkCommunicationCode();
  }, [user?.id]);

  const handleAutoGenerate = async () => {
    const { data, error } = await invoke('regenerate-communication-code', {
      body: { reason: 'initial_onboarding' },
      showErrorToast: true,
    });

    if (error) return;

    toast.success("Communication code generated!", {
      description: `Your security code is: ${data.new_code}`
    });

    localStorage.setItem(`comm-code-onboarding-${user!.id}`, 'completed');
    setShowDialog(false);
  };

  const handleSkip = () => {
    // Generate auto code and mark as completed
    handleAutoGenerate();
  };

  const handleCodeSelected = async (code: string) => {
    setCommunicationCode(code);
  };

  const handleConfirm = async () => {
    if (!communicationCode) {
      toast.error("Please select a communication code");
      return;
    }

    const { error } = await invoke('regenerate-communication-code', {
      body: { 
        custom_code: communicationCode,
        reason: 'initial_onboarding'
      },
      showErrorToast: true,
    });

    if (error) return;

    toast.success("Communication code saved!", {
      description: `Your security code is: ${communicationCode}`
    });

    localStorage.setItem(`comm-code-onboarding-${user!.id}`, 'completed');
    setShowDialog(false);
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <DialogTitle>Choose Your Security Code</DialogTitle>
          </div>
          <DialogDescription className="space-y-2">
            <p>
              Protect yourself from phishing with a unique communication code. 
              This code will appear in every email we send you.
            </p>
            <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-md border border-primary/20 mt-3">
              <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-sm">
                <strong>Anti-Phishing Protection:</strong> If an email claims to be from us but doesn't show your code, it's fake!
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <CommunicationCodeSelector
            onCodeSelected={handleCodeSelected}
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleSkip}
            isLoading={isLoading}
            loadingText="Generating..."
            className="w-full sm:w-auto"
          >
            Auto-Generate for Me
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!communicationCode}
            isLoading={isLoading}
            loadingText="Saving code..."
            className="w-full sm:w-auto"
          >
            Confirm Code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
